import { motion } from "framer-motion";
import { HeartHandshake, MoonStar, Users } from "lucide-react";
import { TiltCard } from "./TiltCard";
import { useLang } from "../store/lang";

const BLESSINGS = [
  {
    Icon: MoonStar,
    title: "A Name Written with Gratitude",
    text: "On this blessed day, Haya Noor Jamal receives her name with thanksgiving to Allah — a prayer whispered into a lifetime.",
    urTitle: "شکرانے کے ساتھ رکھا گیا نام",
    urText: "اس مبارک دن ہایا نور جمال کو اللہ تعالیٰ کا شکر ادا کرتے ہوئے اس کا نام ملا — زندگی بھر کے لیے دعا۔",
  },
  {
    Icon: HeartHandshake,
    title: "A Feast Shared with All",
    text: "The aqiqah sacrifice is cooked and shared with family, friends and those in need — her first act of generosity, done in her name.",
    urTitle: "سب میں بانٹی گئی دعوت",
    urText: "عقیقے کا کھانا رشتہ داروں، دوستوں اور ضرورت مندوں میں بانٹا جاتا ہے — اس کے نام کی سب سے پہلی نیکی۔",
  },
  {
    Icon: Users,
    title: "A Welcome into the Community",
    text: "Gathered under one sky, loved ones bless the child and weave her into the fabric of family, faith and belonging.",
    urTitle: "خاندان میں خیرمقدم",
    urText: "ایک ہی آسمان تلے جمع پیارے بچی کو دعائیں دیتے ہیں اور اسے خاندان، ایمان اور محبت کا حصہ بناتے ہیں۔",
  },
];

export const AqiqahStory = () => {
  const { ur } = useLang();
  return (
  <section id="aqiqah" className="relative py-28 sm:py-36 px-6" data-testid="aqiqah-section">
    <div className="max-w-4xl mx-auto text-center">
      <motion.p
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="eyebrow mb-4"
      >
        {ur ? "مبارک روایت" : "The sacred tradition"}
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, delay: 0.1 }}
        className={`font-display text-3xl sm:text-5xl text-amber-100 font-light ${ur ? "font-urdu" : ""}`}
        dir={ur ? "rtl" : "ltr"}
      >
        {ur ? "عقیقہ کیا ہے؟" : "What is an Aqiqah?"}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.2 }}
        className="mt-6 text-5xl sm:text-6xl text-rose-200/80"
        lang="ar"
        data-testid="aqiqah-arabic"
      >
        عقيقة
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, delay: 0.15 }}
        className={`mt-10 max-w-2xl mx-auto space-y-6 text-slate-300/90 font-light leading-relaxed text-base sm:text-lg ${ur ? "font-urdu" : ""}`}
        dir={ur ? "rtl" : "ltr"}
      >
        <p>
          {ur
            ? "اسلام کے آغاز سے ہی خاندان نومولود کا استقبال عقیقے سے کرتے آئے ہیں — خالص شکرانے کی تقریب۔ یہ وہی لمحہ ہے جب بچے کا نام رکھا جاتا ہے، اور وہ رات کے آسمان میں جلتی چراغ کی مانند دنیا میں آ جاتی ہے۔"
            : "Since the earliest days of Islam, families have welcomed a newborn with the aqiqah — a celebration of pure gratitude. It is the moment a child is given their name, offered to the world like a lantern lit against the night sky."}
        </p>
        <p>
          {ur
            ? "کہا جاتا ہے کہ اس دن بچے کے بالوں کے وزن کے برابر چاندی صدقہ کی جاتی ہے، پیاروں اور ضرورت مندوں کے لیے کھانا تیار ہوتا ہے، اور دعائیں بچے کے گرد اس طرح جمع ہوتی ہیں جیسے چاند کے گرد ستارے۔"
            : "It is said that on this day, locks of the baby's hair are weighed against silver given in charity, a meal is prepared for loved ones and the less fortunate, and prayers gather around the child like stars around the moon."}
        </p>
      </motion.div>

      <motion.blockquote
        initial={{ opacity: 0, y: 24, rotateX: 14 }}
        whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        style={{ transformPerspective: 1000 }}
        className="mt-14 glass-card rounded-3xl px-8 py-10 max-w-2xl mx-auto"
        data-testid="aqiqah-quote"
      >
        <p
          className={`font-display text-2xl sm:text-3xl text-amber-100 italic leading-relaxed ${ur ? "font-urdu not-italic" : ""}`}
          dir={ur ? "rtl" : "ltr"}
        >
          {ur ? "”ہر بچہ اپنے عقیقے کے بدلے گروی ہے۔“" : "“Every child is held in pledge for their aqiqah.”"}
        </p>
        <footer className="mt-4 text-xs tracking-[0.3em] uppercase text-amber-300/70">
          {ur ? "حدیثِ نبوی · ترمذی" : "Prophetic tradition · Tirmidhi"}
        </footer>
      </motion.blockquote>

      <div className="mt-16 grid sm:grid-cols-3 gap-6">
        {BLESSINGS.map((b, i) => (
          <motion.div
            key={b.title}
            initial={{ opacity: 0, y: 30, rotateX: 14 }}
            whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.8, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformPerspective: 900 }}
          >
            <TiltCard max={7} className="glass-card rounded-2xl p-7 h-full text-left" data-testid={`aqiqah-blessing-${i}`}>
              <b.Icon size={20} className="text-amber-300" />
              <h3 className={`mt-4 font-royal text-base text-amber-100 tracking-wide ${ur ? "font-urdu" : ""}`} dir={ur ? "rtl" : "ltr"}>
                {ur ? b.urTitle : b.title}
              </h3>
              <p className={`mt-3 text-sm text-slate-400 font-light leading-relaxed ${ur ? "font-urdu" : ""}`} dir={ur ? "rtl" : "ltr"}>
                {ur ? b.urText : b.text}
              </p>
            </TiltCard>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
  );
};
